import React from 'react';
import './index.scss';

export const DEFAULT_PAGE_LOADER_MESSAGES: ReadonlyArray<string> = [
  'Getting your workspace ready.',
  'Loading your experience.',
  'Almost there.',
];

export const SIGNING_IN_PAGE_LOADER_MESSAGE = 'Signing you in...';
export const WORKSPACE_READY_PAGE_LOADER_MESSAGE = 'Getting your workspace ready.';
export const VERIFYING_AUTH_PAGE_LOADER_MESSAGE = 'Securing your connection.';
export const RESOLVING_ADVISOR_CONTEXT_PAGE_LOADER_MESSAGE = 'Accessing your firm.';
export const PREPARING_ADVISOR_PORTAL_PAGE_LOADER_MESSAGE = 'Preparing your workspace.';

export const INITIAL_APP_LOAD_PAGE_LOADER_MESSAGES: ReadonlyArray<string> = [
  SIGNING_IN_PAGE_LOADER_MESSAGE,
];

export const AUTH_PAGE_LOADER_MESSAGES = INITIAL_APP_LOAD_PAGE_LOADER_MESSAGES;

export const DASHBOARD_DESTINATION_PAGE_LOADER_MESSAGES: ReadonlyArray<string> = [
  'Fetching your client list.',
  "Checking today's meetings.",
  'Preparing your dashboard totals.',
];

export const DASHBOARD_PAGE_LOADER_MESSAGES: ReadonlyArray<string> = [
  WORKSPACE_READY_PAGE_LOADER_MESSAGE,
  ...DASHBOARD_DESTINATION_PAGE_LOADER_MESSAGES,
];

export const PLANNER_DESTINATION_PAGE_LOADER_MESSAGES: ReadonlyArray<string> = [
  'Opening your planning workspace.',
  'Preparing your planning tools.',
  'Loading the latest plan details.',
];

export const CLIENTS_DESTINATION_PAGE_LOADER_MESSAGES: ReadonlyArray<string> = [
  'Fetching your client list.',
];

export const PLANNER_PAGE_LOADER_MESSAGES: ReadonlyArray<string> = [
  WORKSPACE_READY_PAGE_LOADER_MESSAGE,
  ...PLANNER_DESTINATION_PAGE_LOADER_MESSAGES,
];

export const CLIENTS_PAGE_LOADER_MESSAGES: ReadonlyArray<string> = [
  WORKSPACE_READY_PAGE_LOADER_MESSAGE,
  ...CLIENTS_DESTINATION_PAGE_LOADER_MESSAGES,
];

export type PageLoaderDestination = 'dashboard' | 'planner' | 'clients';

export type PageLoaderBranding = {
  logoAlt?: string;
  logoUri?: string;
  watermarkLogoUri?: string;
};

export type PageLoaderBrandingSource = {
  compact_logo_light_uri?: string | null;
  compact_logo_uri?: string | null;
  light_text_logo_uri?: string | null;
  logo_light_uri?: string | null;
  logo_mark_uri?: string | null;
  logo_uri?: string | null;
  name?: string | null;
  text_logo_uri?: string | null;
};

const INITIAL_PROGRESS_LINE_DELAY_MS = 520;
const GREETING_REVEAL_DELAY_MS = 620;
const FIRST_STEP_AFTER_GREETING_DELAY_MS = 1180;
const CHECKMARK_TO_NEXT_LINE_DELAY_MS = 380;
const COMPLETION_HOLD_MS = 700;
const READY_HOLD_MS = 780;
const HANDOFF_MS = 780;
const REASSURE_AFTER_MS = 4600;
const RETRY_AFTER_MS = 10500;
const ENTERED_ANIMATION_DELAY_MS = 700;
const DEFAULT_PROGRESS_LINE_HOLD_MS = 1400;
const MAX_PROGRESS_METER_STEPS = 4;
const MAX_WORKING_PROGRESS_LINES = MAX_PROGRESS_METER_STEPS;
const MAX_REMEMBERED_LOADER_ANIMATION_KEYS = 30;
const SIGNING_IN_STATUS_LABEL = 'Signing in';
const DEFAULT_STATUS_LABEL = 'Loading';
const READY_STATUS_LABEL = 'Ready';
const DEFAULT_HEADLINE = 'Loading your experience';
const GREETING_MESSAGE_PATTERN = /^(Good (?:morning|afternoon|evening))(?:,\s*(.+))?$/;
const PROGRESS_LINE_HOLD_MS_BY_MESSAGE: Readonly<Record<string, number>> = {
  'Securing your connection': 1250,
  'Accessing your firm': 1350,
  'Preparing your workspace': 1450,
  "Checking today's meetings": 1450,
  'Preparing your dashboard totals': 1650,
  'Opening your planning workspace': 1500,
  'Preparing your planning tools': 1450,
  'Loading the latest plan details': 1600,
  'Almost there': 1350,
};
const rememberedEntryAnimationKeys = new Set<string>();
const rememberedHeadlineRevealTimings = new Map<string, HeadlineRevealTiming>();
const rememberedLoadedImageUris = new Set<string>();
const rememberedProgressTimelines = new Map<string, ProgressTimelineMemory>();
const HEADER_LOGO_FIELDS: ReadonlyArray<keyof PageLoaderBrandingSource> = [
  'logo_uri',
  'logo_light_uri',
  'text_logo_uri',
  'light_text_logo_uri',
  'compact_logo_uri',
  'compact_logo_light_uri',
  'logo_mark_uri',
];
const WATERMARK_LOGO_FIELDS: ReadonlyArray<keyof PageLoaderBrandingSource> = [
  'logo_mark_uri',
  'compact_logo_uri',
  'compact_logo_light_uri',
  'logo_uri',
  'text_logo_uri',
  'logo_light_uri',
  'light_text_logo_uri',
];

type LoaderGreeting = {
  message: string;
  name?: string;
  salutation: string;
};

type ProgressLineTiming = {
  holdMs: number;
  revealAt: number;
};

type HeadlineRevealTiming = {
  scheduledAt: number;
  revealedAt?: number;
};

type ProgressTimelineMemory = {
  maxProgressPercent: number;
  timingByLineKey: Map<string, ProgressLineTiming>;
};

const isValidStartedAt = (startedAt: number | undefined): startedAt is number =>
  typeof startedAt === 'number' && Number.isFinite(startedAt);

const rememberAnimationKey = (animationKeys: Set<string>, animationKey: string | undefined) => {
  if (!animationKey || animationKeys.has(animationKey)) {
    return;
  }

  animationKeys.add(animationKey);

  if (animationKeys.size <= MAX_REMEMBERED_LOADER_ANIMATION_KEYS) {
    return;
  }

  const oldestKey = animationKeys.values().next().value;

  if (oldestKey) {
    animationKeys.delete(oldestKey);
  }
};

const sanitizeBrandingText = (value?: string | null): string => value?.trim() ?? '';

const getSafePageLoaderImageUri = (uri?: string | null): string | undefined => {
  const trimmedUri = sanitizeBrandingText(uri);
  const normalizedUri = trimmedUri.toLowerCase();

  if (
    normalizedUri.startsWith('https://') ||
    normalizedUri.startsWith('http://') ||
    normalizedUri.startsWith('data:image/')
  ) {
    return trimmedUri;
  }

  return undefined;
};

const getFirstSafeBrandingImageUri = (
  sources: ReadonlyArray<PageLoaderBrandingSource | null | undefined>,
  fields: ReadonlyArray<keyof PageLoaderBrandingSource>,
): string | undefined => {
  for (const source of sources) {
    if (!source) {
      continue;
    }

    for (const field of fields) {
      const uri = getSafePageLoaderImageUri(source[field]);

      if (uri) {
        return uri;
      }
    }
  }

  return undefined;
};

const getFirstBrandName = (
  sources: ReadonlyArray<PageLoaderBrandingSource | null | undefined>,
): string | undefined => {
  for (const source of sources) {
    const name = sanitizeBrandingText(source?.name);

    if (name) {
      return name;
    }
  }

  return undefined;
};

export const buildPageLoaderBranding = (
  ...sources: ReadonlyArray<PageLoaderBrandingSource | null | undefined>
): PageLoaderBranding | undefined => {
  const logoUri = getFirstSafeBrandingImageUri(sources, HEADER_LOGO_FIELDS);
  const watermarkLogoUri = getFirstSafeBrandingImageUri(sources, WATERMARK_LOGO_FIELDS);

  if (!logoUri && !watermarkLogoUri) {
    return undefined;
  }

  const brandName = getFirstBrandName(sources);

  return {
    logoAlt: brandName ? `${brandName} logo` : 'Company logo',
    logoUri,
    watermarkLogoUri,
  };
};

const getHeadlineRevealKey = (
  startedAt: number,
  headlineMessage: string | undefined,
): string | undefined => (headlineMessage ? `${startedAt}:${headlineMessage}` : undefined);

const getEntryAnimationKey = (startedAt: number): string => `${startedAt}`;

const getProgressTimelineKey = (startedAt: number, isSigningIn: boolean): string =>
  `${startedAt}:${isSigningIn ? 'signing-in' : 'loading'}`;

const hasRememberedEntryAnimation = (entryAnimationKey: string | undefined): boolean =>
  entryAnimationKey !== undefined && rememberedEntryAnimationKeys.has(entryAnimationKey);

const hasRememberedHeadlineReveal = (headlineRevealKey: string | undefined): boolean =>
  headlineRevealKey !== undefined &&
  rememberedHeadlineRevealTimings.get(headlineRevealKey)?.revealedAt !== undefined;

const hasRememberedLoadedImageUri = (imageUri: string | undefined): boolean =>
  imageUri !== undefined && rememberedLoadedImageUris.has(imageUri);

const getOrCreateHeadlineRevealTiming = (headlineRevealKey: string): HeadlineRevealTiming => {
  const rememberedTiming = rememberedHeadlineRevealTimings.get(headlineRevealKey);

  if (rememberedTiming) {
    return rememberedTiming;
  }

  const timing = { scheduledAt: Date.now() + GREETING_REVEAL_DELAY_MS };
  rememberedHeadlineRevealTimings.set(headlineRevealKey, timing);

  if (rememberedHeadlineRevealTimings.size > MAX_REMEMBERED_LOADER_ANIMATION_KEYS) {
    const oldestKey = rememberedHeadlineRevealTimings.keys().next().value;

    if (oldestKey) {
      rememberedHeadlineRevealTimings.delete(oldestKey);
    }
  }

  return timing;
};

const rememberEntryAnimation = (entryAnimationKey: string | undefined) => {
  rememberAnimationKey(rememberedEntryAnimationKeys, entryAnimationKey);
};

const rememberLoadedImageUri = (imageUri: string | undefined) => {
  rememberAnimationKey(rememberedLoadedImageUris, imageUri);
};

const rememberProgressTimeline = (
  timelineKey: string,
  progressTimeline: ProgressTimelineMemory,
) => {
  rememberedProgressTimelines.set(timelineKey, progressTimeline);

  if (rememberedProgressTimelines.size <= MAX_REMEMBERED_LOADER_ANIMATION_KEYS) {
    return;
  }

  const oldestKey = rememberedProgressTimelines.keys().next().value;

  if (oldestKey) {
    rememberedProgressTimelines.delete(oldestKey);
  }
};

export const resetPageLoaderAnimationMemoryForTests = () => {
  rememberedEntryAnimationKeys.clear();
  rememberedHeadlineRevealTimings.clear();
  rememberedLoadedImageUris.clear();
  rememberedProgressTimelines.clear();
};

const getDestinationPageLoaderMessages = (
  destination: PageLoaderDestination | undefined,
): ReadonlyArray<string> => {
  switch (destination) {
    case 'dashboard':
      return DASHBOARD_DESTINATION_PAGE_LOADER_MESSAGES;
    case 'planner':
      return PLANNER_DESTINATION_PAGE_LOADER_MESSAGES;
    case 'clients':
      return CLIENTS_DESTINATION_PAGE_LOADER_MESSAGES;
    default:
      return [];
  }
};

const getFirstName = (name: string | undefined): string | undefined => {
  const [firstName] = name?.trim().split(/\s+/) ?? [];
  return firstName || undefined;
};

const getGreetingPeriod = (now: Date): 'morning' | 'afternoon' | 'evening' => {
  const hour = now.getHours();

  if (hour < 12) {
    return 'morning';
  }

  if (hour < 17) {
    return 'afternoon';
  }

  return 'evening';
};

export const buildAdvisorGreeting = (advisorName: string | undefined, now = new Date()): string => {
  const firstName = getFirstName(advisorName);
  const greeting = `Good ${getGreetingPeriod(now)}`;

  return firstName ? `${greeting}, ${firstName}` : `${greeting}`;
};

export const buildAuthPageLoaderMessages = ({
  now,
}: {
  now?: Date;
} = {}): ReadonlyArray<string> => [
  SIGNING_IN_PAGE_LOADER_MESSAGE,
  buildAdvisorGreeting(undefined, now),
  VERIFYING_AUTH_PAGE_LOADER_MESSAGE,
  RESOLVING_ADVISOR_CONTEXT_PAGE_LOADER_MESSAGE,
  PREPARING_ADVISOR_PORTAL_PAGE_LOADER_MESSAGE,
];

export const getAdvisorNameFromUser = (user: unknown): string | undefined => {
  if (!user || typeof user !== 'object') {
    return undefined;
  }

  const name = (user as { name?: unknown }).name;
  return typeof name === 'string' && name.trim().length > 0 ? name : undefined;
};

const getLoaderGreeting = (messages: ReadonlyArray<string>): LoaderGreeting | undefined => {
  for (const message of messages) {
    const match = message.match(GREETING_MESSAGE_PATTERN);

    if (!match) {
      continue;
    }

    const [, greeting, name] = match;

    return {
      message,
      name,
      salutation: name ? `${greeting},` : greeting,
    };
  }

  return undefined;
};

const normalizeVisibleLoaderMessage = (message: string): string => message.replace(/\.+$/, '');

const getProgressMessages = (
  messages: ReadonlyArray<string>,
  greeting: LoaderGreeting | undefined,
  fallbackHeadline: string | undefined,
): ReadonlyArray<string> => {
  const signingInMessage = normalizeVisibleLoaderMessage(SIGNING_IN_PAGE_LOADER_MESSAGE);
  const progressMessages = messages
    .filter((message) => message !== greeting?.message)
    .map(normalizeVisibleLoaderMessage)
    .filter((message) => message !== fallbackHeadline);
  const hasKnownCheckpoint = progressMessages.some((message) => message !== signingInMessage);

  return hasKnownCheckpoint
    ? progressMessages
    : progressMessages.filter((message) => message !== signingInMessage);
};

const getLoginHandoffProgressMessages = (
  progressMessages: ReadonlyArray<string>,
): ReadonlyArray<string> => {
  const signingInMessage = normalizeVisibleLoaderMessage(SIGNING_IN_PAGE_LOADER_MESSAGE);
  const secureSessionMessage = normalizeVisibleLoaderMessage(VERIFYING_AUTH_PAGE_LOADER_MESSAGE);
  const firmWorkspaceMessage = normalizeVisibleLoaderMessage(
    RESOLVING_ADVISOR_CONTEXT_PAGE_LOADER_MESSAGE,
  );
  const openingPortalMessage = normalizeVisibleLoaderMessage(
    PREPARING_ADVISOR_PORTAL_PAGE_LOADER_MESSAGE,
  );
  const stagedMessages = [
    signingInMessage,
    secureSessionMessage,
    firmWorkspaceMessage,
    openingPortalMessage,
  ].filter((message) => progressMessages.includes(message));
  const fallbackMessages = progressMessages.filter((message) => !stagedMessages.includes(message));

  return [...stagedMessages, ...fallbackMessages]
    .filter((message): message is string => Boolean(message))
    .filter((message, index, messages) => messages.indexOf(message) === index)
    .slice(0, MAX_WORKING_PROGRESS_LINES);
};

const getVisualProgressMessages = (
  progressMessages: ReadonlyArray<string>,
  isSigningIn: boolean,
): ReadonlyArray<string> => {
  if (progressMessages.length <= MAX_WORKING_PROGRESS_LINES) {
    return progressMessages;
  }

  if (isSigningIn) {
    return getLoginHandoffProgressMessages(progressMessages);
  }

  return progressMessages.slice(0, MAX_WORKING_PROGRESS_LINES);
};

const getCompletedLineCount = (progressMessages: ReadonlyArray<string>): number =>
  Math.max(0, progressMessages.length - 1);

const getProgressLineCount = ({
  isSigningIn,
  progressMessages,
}: {
  isSigningIn: boolean;
  progressMessages: ReadonlyArray<string>;
}): number => {
  if (progressMessages.length === 0) {
    return 0;
  }

  if (isSigningIn) {
    return MAX_PROGRESS_METER_STEPS;
  }

  return Math.min(progressMessages.length, MAX_PROGRESS_METER_STEPS);
};

const getProgressMeterLabel = ({
  currentLineCount,
  isIndeterminate,
  isSigningIn,
  isReady,
  totalLineCount,
}: {
  currentLineCount: number;
  isIndeterminate: boolean;
  isSigningIn: boolean;
  isReady?: boolean;
  totalLineCount: number;
}): string => {
  if (isReady) {
    return READY_STATUS_LABEL;
  }

  if (isIndeterminate) {
    return isSigningIn ? 'Authenticating' : 'Connecting';
  }

  return `Step ${Math.min(Math.max(currentLineCount, 1), totalLineCount)} of ${totalLineCount}`;
};

const getScreenReaderLabel = ({
  currentLineCount,
  isIndeterminate,
  isSigningIn,
  isReady,
  progressMessages,
  stallLevel,
  totalLineCount,
}: {
  currentLineCount: number;
  isIndeterminate: boolean;
  isSigningIn: boolean;
  isReady?: boolean;
  progressMessages: ReadonlyArray<string>;
  stallLevel?: number;
  totalLineCount: number;
}): string => {
  if (isReady) {
    return 'Your workspace is ready.';
  }

  if (isIndeterminate) {
    if (stallLevel && stallLevel > 0) {
      return isSigningIn
        ? 'Still signing you in. This is taking longer than usual.'
        : 'Still loading. This is taking longer than usual.';
    }

    return isSigningIn ? 'Signing you in.' : 'Loading.';
  }

  const announcedLineCount = Math.min(Math.max(currentLineCount, 1), totalLineCount);

  if (stallLevel && stallLevel > 0) {
    return `Still working. Step ${announcedLineCount} of ${totalLineCount} is taking longer than usual.`;
  }

  const activeMessage =
    progressMessages[Math.min(announcedLineCount - 1, progressMessages.length - 1)] ??
    DEFAULT_HEADLINE;

  return `Step ${announcedLineCount} of ${totalLineCount}. ${activeMessage}.`;
};

const getInitialProgressLineDelay = (hasGreeting: boolean): number =>
  hasGreeting
    ? GREETING_REVEAL_DELAY_MS + FIRST_STEP_AFTER_GREETING_DELAY_MS
    : INITIAL_PROGRESS_LINE_DELAY_MS;

const getInitialProgressLineRevealAt = ({
  hasGreeting,
  isSigningIn,
  mountedAt,
  startedAt,
}: {
  hasGreeting: boolean;
  isSigningIn: boolean;
  mountedAt: number;
  startedAt: number;
}): number => {
  if (isSigningIn) {
    return mountedAt + getInitialProgressLineDelay(hasGreeting);
  }

  return startedAt + getInitialProgressLineDelay(hasGreeting);
};

const getProgressLineHoldMs = (
  message: string,
  progressMessages: ReadonlyArray<string>,
  destination?: PageLoaderDestination,
): number => {
  if (message === 'Getting your workspace ready') {
    if (progressMessages.includes('Almost there')) {
      return 1250;
    }

    return destination === 'clients' ? 1300 : 1400;
  }

  if (message === 'Fetching your client list') {
    return destination === 'clients' ? 1500 : 1550;
  }

  return PROGRESS_LINE_HOLD_MS_BY_MESSAGE[message] ?? DEFAULT_PROGRESS_LINE_HOLD_MS;
};

export const buildInitialAppLoadPageLoaderMessages = ({
  advisorName,
  destination,
  destinationMessages,
  now,
}: {
  advisorName?: string;
  destination?: PageLoaderDestination;
  destinationMessages?: ReadonlyArray<string>;
  now?: Date;
}): ReadonlyArray<string> => {
  const firstName = getFirstName(advisorName);

  if (!firstName && destination === undefined && destinationMessages === undefined) {
    return buildAuthPageLoaderMessages({ now });
  }

  const messages = [
    SIGNING_IN_PAGE_LOADER_MESSAGE,
    buildAdvisorGreeting(advisorName, now),
    VERIFYING_AUTH_PAGE_LOADER_MESSAGE,
    RESOLVING_ADVISOR_CONTEXT_PAGE_LOADER_MESSAGE,
    PREPARING_ADVISOR_PORTAL_PAGE_LOADER_MESSAGE,
    WORKSPACE_READY_PAGE_LOADER_MESSAGE,
  ];
  const checkpointMessages =
    destinationMessages ?? getDestinationPageLoaderMessages(destination).slice(0, 1);

  return [...messages, ...checkpointMessages];
};

/**
 * Full-screen loading surface for advisor portal route transitions.
 *
 * Must only ever be reached through a static import. As the fallback for the
 * route-level Suspense boundary it has to be present before any lazy chunk
 * resolves; loading it lazily would leave the wait it exists to cover uncovered.
 */
interface WavvestPageLoaderProps {
  animationStartedAt?: number;
  branding?: PageLoaderBranding;
  destination?: PageLoaderDestination;
  isReady?: boolean;
  messages?: ReadonlyArray<string>;
  onHandoffComplete?: () => void;
  showFallbackBranding?: boolean;
}

const WavvestMark: React.FC<{ className?: string; monochrome?: boolean }> = ({
  className,
  monochrome = false,
}) => {
  const accentFill = monochrome ? 'currentColor' : 'var(--wv-pl-blue)';

  return (
    <svg className={className} viewBox="0 0 27 17.76" fill="none" focusable="false">
      <path
        d="M8.996 0C4.028 0 0 3.976 0 8.879h4.5c0-2.451 2.012-4.442 4.5-4.442h6.749V0H8.996Z"
        fill="currentColor"
      />
      <path
        d="M8.996 8.879C4.028 8.879 0 12.856 0 17.759h4.5c0-2.452 2.012-4.442 4.5-4.442h6.749V8.875H8.996Z"
        fill={accentFill}
      />
      <path
        d="M18.006 13.317h-6.748v4.442h6.748c4.968 0 8.996-3.976 8.996-8.88h-4.5c0 2.452-2.012 4.443-4.5 4.443Z"
        fill={accentFill}
      />
      <path
        d="M27.002 0h-4.5c0 2.452-2.013 4.442-4.501 4.442h-6.748v4.442h6.748c4.968 0 8.996-3.976 8.996-8.879L27.002 0Z"
        fill="currentColor"
      />
    </svg>
  );
};

const WavvestWordmark: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 126.899 21.4018" fill="none" focusable="false">
    <path
      d="M39.993 3.86452V5.66509C39.4908 5.12833 38.8951 4.68429 38.2157 4.32808C37.1867 3.78645 36.0149 3.51319 34.7052 3.51319C33.1051 3.51319 31.6675 3.91332 30.3923 4.70381C29.1221 5.49918 28.1128 6.56781 27.3742 7.91457C26.6357 9.26134 26.2665 10.774 26.2665 12.4575C26.2665 14.1409 26.6357 15.6536 27.3742 17.0003C28.1128 18.3471 29.1221 19.4109 30.3923 20.1916C31.6675 20.9723 33.1051 21.3627 34.7052 21.3627C36.0149 21.3627 37.1916 21.0943 38.2353 20.5527C38.9099 20.2013 39.5007 19.7622 39.993 19.2303V21.392H43.3705V3.86452H39.993ZM39.1806 16.6588C38.1861 17.7518 36.8765 18.2983 35.2517 18.2983C34.1587 18.2983 33.1888 18.0446 32.342 17.542C31.4902 17.0345 30.8305 16.3465 30.3578 15.4682C29.8852 14.5898 29.6489 13.5749 29.6489 12.4233C29.6489 11.2717 29.8852 10.286 30.3578 9.40772C30.8305 8.53428 31.4853 7.84138 32.3223 7.33878C33.1593 6.83131 34.1243 6.57757 35.2124 6.57757C36.3004 6.57757 37.2556 6.82643 38.068 7.31926C38.8803 7.8121 39.5204 8.505 39.9782 9.39308C40.121 9.66146 40.2343 9.94936 40.3327 10.247L40.3475 10.3397C40.5395 10.9838 40.638 11.6914 40.638 12.4575C40.638 13.4285 40.4854 14.2971 40.1801 15.068V15.1022C39.9339 15.6731 39.5991 16.1952 39.1806 16.6588Z"
      fill="currentColor"
    />
    <path
      d="M18.3447 16.2196L14.3863 3.86456H11.0925L7.13406 16.2196L3.62365 3.86456H0L5.58318 21.392H8.66032L12.7419 8.67094L16.8135 21.392H19.8956L25.4788 3.86456H21.8502L18.3447 16.2196Z"
      fill="currentColor"
    />
    <path
      d="M54.3524 16.8102L49.6653 3.86468H45.6429L52.6735 21.3921H56.115L62.7813 3.86468H58.8327L54.3524 16.8102Z"
      fill="currentColor"
    />
    <path
      d="M77.4445 3.86453L72.9641 16.8101L68.277 3.86453H64.2546L71.2852 21.392H74.7267L81.3931 3.86453H77.4445Z"
      fill="currentColor"
    />
    <path
      d="M106.378 7.19738C106.876 6.87045 107.417 6.70455 108.008 6.70455C108.978 6.70455 109.781 6.90949 110.421 7.31937C111.061 7.72926 111.543 8.14402 111.873 8.56855L114.601 6.56792C114.01 5.74815 113.193 5.04061 112.154 4.44042C111.115 3.84511 109.859 3.54258 108.397 3.54258C107.166 3.54258 106.103 3.7768 105.207 4.24524C104.306 4.71368 103.621 5.33339 103.149 6.10924C102.676 6.88021 102.44 7.73902 102.44 8.6759C102.44 10.2471 102.903 11.3938 103.823 12.1209C104.744 12.8479 106.058 13.4335 107.757 13.8775C108.678 14.1117 109.387 14.3508 109.884 14.5997C110.381 14.8486 110.731 15.1072 110.928 15.3902C111.13 15.6732 111.228 16.0001 111.228 16.3759C111.228 16.937 111.021 17.4103 110.608 17.8007C110.194 18.1911 109.608 18.3814 108.855 18.3814C107.91 18.3814 107.142 18.1862 106.551 17.8007C105.96 17.4152 105.335 16.7858 104.67 15.9221L101.977 17.6787C102.735 19.0352 103.695 19.9965 104.867 20.5577C106.039 21.1188 107.225 21.4018 108.432 21.4018C109.52 21.4018 110.529 21.1871 111.464 20.7529C112.4 20.3186 113.153 19.6989 113.734 18.8889C114.315 18.0788 114.601 17.1127 114.601 15.9904C114.601 14.7022 114.162 13.6677 113.291 12.8821C112.415 12.0965 110.938 11.4573 108.86 10.9693C107.629 10.6863 106.787 10.3642 106.324 10.0031C105.862 9.64205 105.635 9.16385 105.635 8.57831C105.635 7.99276 105.881 7.53408 106.378 7.20714V7.19738Z"
      fill="currentColor"
    />
    <path
      d="M126.899 7.09491V3.86463H123.212V0H120.341L119.667 3.86463H117.008V7.09491H119.667V15.9123C119.667 17.7617 120.11 19.1279 120.996 20.0063C121.882 20.8846 123.084 21.3238 124.595 21.3238C125.092 21.3238 125.521 21.2896 125.89 21.2164C126.254 21.1481 126.594 21.0651 126.899 20.9724V17.7031C126.663 17.7714 126.392 17.83 126.082 17.8788C125.777 17.9276 125.422 17.9471 125.019 17.9471C124.497 17.9471 124.068 17.7812 123.724 17.4543C123.379 17.1273 123.212 16.6345 123.212 15.9806V7.09491H126.899Z"
      fill="currentColor"
    />
    <path
      d="M94.2912 17.4933C93.533 18.0788 92.4597 18.3716 91.0664 18.3716C89.55 18.3716 88.3683 17.9081 87.5215 16.9809C87.0046 16.4149 86.655 15.7318 86.4581 14.9998L98.8947 11.6964L99.5347 11.5256C99.4904 10.9303 99.4018 10.3642 99.2688 9.82748C99.1654 9.38831 99.0325 8.96867 98.8651 8.56855C98.7617 8.30017 98.6436 8.04643 98.5106 7.79757C97.8017 6.46057 96.8071 5.42122 95.532 4.66976C94.2568 3.91831 92.765 3.54258 91.0664 3.54258C89.3678 3.54258 87.9105 3.91831 86.6353 4.66976C85.3552 5.42122 84.3656 6.46057 83.6566 7.79757C82.9476 9.1297 82.5932 10.7009 82.5932 12.5015C82.5932 14.3021 82.9476 15.8391 83.6566 17.1761C84.3656 18.5131 85.3552 19.5476 86.6353 20.2844C87.9105 21.0212 89.3875 21.3921 91.0664 21.3921C92.9767 21.3921 94.567 21.0212 95.8323 20.2844C97.0976 19.5476 98.1315 18.4399 98.934 16.9663L96.2754 15.3121C95.7092 16.1807 95.0445 16.9077 94.2912 17.4933ZM87.556 7.77805C88.4274 6.85581 89.5992 6.39225 91.0664 6.39225C92.7207 6.39225 93.9466 6.96317 94.7491 8.10987C94.9805 8.4368 95.1676 8.79789 95.3252 9.17849L86.2119 11.5988C86.2217 11.4036 86.2365 11.2133 86.2562 11.023C86.3694 9.84212 86.7239 8.66126 87.556 7.77805Z"
      fill="currentColor"
    />
  </svg>
);

const LoaderCheckIcon: React.FC = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M2.6 8.4 6.1 11.9 13.4 4.4" />
  </svg>
);

interface ProgressLineProps {
  isComplete: boolean;
  isVisible: boolean;
  message: string;
}

const ProgressLine: React.FC<ProgressLineProps> = ({ isComplete, isVisible, message }) => {
  const initiallyCompleteRef = React.useRef(isComplete);
  const wasCompleteRef = React.useRef(false);
  const shouldAnimateCheck = isComplete && !wasCompleteRef.current && !initiallyCompleteRef.current;
  const lineClassName = [
    'wv-page-loader__line',
    isVisible ? 'is-visible' : undefined,
    isComplete ? 'wv-page-loader__line--complete is-complete' : 'wv-page-loader__line--active',
  ]
    .filter(Boolean)
    .join(' ');
  const checkClassName = [
    'wv-page-loader__line-check',
    shouldAnimateCheck ? 'wv-page-loader__line-check--animate' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  React.useEffect(() => {
    wasCompleteRef.current = isComplete;
  }, [isComplete]);

  return (
    <li className={lineClassName}>
      <span className="wv-page-loader__line-status" aria-hidden="true">
        <span className="wv-page-loader__line-dots">
          <span className="wv-page-loader__line-dot" />
          <span className="wv-page-loader__line-dot" />
          <span className="wv-page-loader__line-dot" />
        </span>
        <span className={checkClassName}>
          <LoaderCheckIcon />
        </span>
      </span>
      <span className="wv-page-loader__line-copy">{message}</span>
    </li>
  );
};

export const WavvestPageLoader: React.FC<WavvestPageLoaderProps> = ({
  animationStartedAt,
  branding,
  destination,
  isReady = false,
  messages,
  onHandoffComplete,
  showFallbackBranding = true,
}) => {
  const mountedAt = React.useRef(Date.now()).current;
  const lineRevealTimingByKey = React.useRef(new Map<string, ProgressLineTiming>());
  const startedAt = isValidStartedAt(animationStartedAt) ? animationStartedAt : mountedAt;
  const entryAnimationKey = getEntryAnimationKey(startedAt);
  const [isEntered, setIsEntered] = React.useState(
    () =>
      hasRememberedEntryAnimation(entryAnimationKey) ||
      (isValidStartedAt(animationStartedAt) &&
        Date.now() - animationStartedAt >= ENTERED_ANIMATION_DELAY_MS),
  );
  const [isHandoff, setIsHandoff] = React.useState(false);
  const [isVisualReady, setIsVisualReady] = React.useState(false);
  const [stallLevel, setStallLevel] = React.useState(0);
  const [animatedVisibleLineCount, setAnimatedVisibleLineCount] = React.useState(0);
  const [animatedCompletedLineCount, setAnimatedCompletedLineCount] = React.useState(0);
  const [revealedHeadlineMessage, setRevealedHeadlineMessage] = React.useState<
    string | undefined
  >();
  const [failedBrandLogoUri, setFailedBrandLogoUri] = React.useState<string | undefined>();
  const [failedWatermarkLogoUri, setFailedWatermarkLogoUri] = React.useState<string | undefined>();
  const progressPercentRef = React.useRef<{
    percent: number;
    signature: string;
  }>();
  const messageList = React.useMemo(() => {
    const trimmedMessages = messages
      ?.map((message) => message.trim())
      .filter((message) => message.length > 0);

    return trimmedMessages && trimmedMessages.length > 0
      ? trimmedMessages
      : DEFAULT_PAGE_LOADER_MESSAGES;
  }, [messages]);
  const animationOffsetMs = React.useMemo(
    () => (isValidStartedAt(animationStartedAt) ? Math.min(0, animationStartedAt - mountedAt) : 0),
    [animationStartedAt, mountedAt],
  );
  const isSigningIn = messageList.includes(SIGNING_IN_PAGE_LOADER_MESSAGE);
  const progressTimelineKey = getProgressTimelineKey(startedAt, isSigningIn);
  const greeting = React.useMemo(() => getLoaderGreeting(messageList), [messageList]);
  const isAwaitingSignIn = isSigningIn && !greeting?.name;
  const fallbackHeadline = isSigningIn ? undefined : DEFAULT_HEADLINE;
  const headlineMessage = greeting?.message ?? fallbackHeadline;
  const statusLabel = isSigningIn ? SIGNING_IN_STATUS_LABEL : DEFAULT_STATUS_LABEL;
  const safeBrandLogoUri = getSafePageLoaderImageUri(branding?.logoUri);
  const safeWatermarkLogoUri = getSafePageLoaderImageUri(branding?.watermarkLogoUri);
  const brandLogoUri =
    safeBrandLogoUri && safeBrandLogoUri !== failedBrandLogoUri ? safeBrandLogoUri : undefined;
  const watermarkLogoUri =
    safeWatermarkLogoUri && safeWatermarkLogoUri !== failedWatermarkLogoUri
      ? safeWatermarkLogoUri
      : undefined;
  const [loadedBrandLogoUri, setLoadedBrandLogoUri] = React.useState<string | undefined>(() =>
    hasRememberedLoadedImageUri(brandLogoUri) ? brandLogoUri : undefined,
  );
  const [loadedWatermarkLogoUri, setLoadedWatermarkLogoUri] = React.useState<string | undefined>(
    () => (hasRememberedLoadedImageUri(watermarkLogoUri) ? watermarkLogoUri : undefined),
  );
  const rawProgressMessages = React.useMemo(
    () => getProgressMessages(messageList, greeting, fallbackHeadline),
    [fallbackHeadline, greeting, messageList],
  );
  const progressMessages = React.useMemo(
    () => getVisualProgressMessages(rawProgressMessages, isSigningIn),
    [isSigningIn, rawProgressMessages],
  );
  const headlineRevealKey = getHeadlineRevealKey(startedAt, headlineMessage);
  const isHeadlineRevealRemembered = hasRememberedHeadlineReveal(headlineRevealKey);
  const headlineRevealedAt = headlineRevealKey
    ? rememberedHeadlineRevealTimings.get(headlineRevealKey)?.revealedAt
    : undefined;
  // Resume the same reveal after a route replaces the loader. The offset stays
  // fixed for this mount, so later checkpoint renders cannot restart the motion.
  const headlineAnimationOffsetMs =
    headlineRevealedAt !== undefined ? Math.min(0, headlineRevealedAt - mountedAt) : 0;
  const isHeadlineRevealed =
    headlineMessage !== undefined &&
    (isHeadlineRevealRemembered || revealedHeadlineMessage === headlineMessage);

  React.useEffect(() => {
    if (isEntered) {
      rememberEntryAnimation(entryAnimationKey);
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      rememberEntryAnimation(entryAnimationKey);
      setIsEntered(true);
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [entryAnimationKey, isEntered]);

  React.useEffect(() => {
    if (!headlineMessage || !headlineRevealKey || !isEntered) {
      setRevealedHeadlineMessage(undefined);
      return undefined;
    }

    if (hasRememberedHeadlineReveal(headlineRevealKey)) {
      setRevealedHeadlineMessage(headlineMessage);
      return undefined;
    }

    setRevealedHeadlineMessage(undefined);
    const revealTiming = getOrCreateHeadlineRevealTiming(headlineRevealKey);
    // Let the status establish itself before the greeting wipes in. A bounded
    // font wait prevents a late serif swap from interrupting that reveal.
    let cancelled = false;
    let fontTimer = 0;
    const fontsSettled = document.fonts
      ? Promise.race([
          document.fonts.ready,
          new Promise<void>((resolve) => {
            fontTimer = window.setTimeout(resolve, 700);
          }),
        ])
      : undefined;
    const revealTimer = window.setTimeout(
      () => {
        const reveal = () => {
          if (!cancelled) {
            revealTiming.revealedAt ??= Date.now();
            setRevealedHeadlineMessage(headlineMessage);
          }
        };

        if (fontsSettled) {
          void fontsSettled.then(reveal, reveal);
        } else {
          reveal();
        }
      },
      Math.max(0, revealTiming.scheduledAt - Date.now()),
    );

    return () => {
      cancelled = true;
      window.clearTimeout(revealTimer);
      window.clearTimeout(fontTimer);
    };
  }, [headlineMessage, headlineRevealKey, isEntered]);

  const handleBrandLogoError = React.useCallback(() => {
    setFailedBrandLogoUri(brandLogoUri);
  }, [brandLogoUri]);
  const handleWatermarkLogoError = React.useCallback(() => {
    setFailedWatermarkLogoUri(watermarkLogoUri);
  }, [watermarkLogoUri]);
  const handleBrandLogoLoad = React.useCallback(() => {
    rememberLoadedImageUri(brandLogoUri);
    setLoadedBrandLogoUri(brandLogoUri);
  }, [brandLogoUri]);
  const handleWatermarkLogoLoad = React.useCallback(() => {
    rememberLoadedImageUri(watermarkLogoUri);
    setLoadedWatermarkLogoUri(watermarkLogoUri);
  }, [watermarkLogoUri]);

  React.useEffect(() => {
    setLoadedBrandLogoUri((currentUri) =>
      currentUri === brandLogoUri || hasRememberedLoadedImageUri(brandLogoUri)
        ? brandLogoUri
        : undefined,
    );
  }, [brandLogoUri]);

  React.useEffect(() => {
    setLoadedWatermarkLogoUri((currentUri) =>
      currentUri === watermarkLogoUri || hasRememberedLoadedImageUri(watermarkLogoUri)
        ? watermarkLogoUri
        : undefined,
    );
  }, [watermarkLogoUri]);

  const lineRevealTimings = React.useMemo(() => {
    const activeLineKeys = new Set(
      progressMessages.map((message, lineIndex) => `${lineIndex}-${message}`),
    );
    const rememberedTimingByKey =
      rememberedProgressTimelines.get(progressTimelineKey)?.timingByLineKey;
    const committedTimingByKey =
      lineRevealTimingByKey.current.size > 0
        ? lineRevealTimingByKey.current
        : (rememberedTimingByKey ?? lineRevealTimingByKey.current);
    const hasExistingLines = Array.from(committedTimingByKey.keys()).some((lineKey) =>
      activeLineKeys.has(lineKey),
    );
    const now = Date.now();
    const initialRevealAt = getInitialProgressLineRevealAt({
      hasGreeting: Boolean(greeting),
      isSigningIn,
      mountedAt,
      startedAt,
    });
    // Auth checkpoints can arrive after the shell has been sitting in an
    // indeterminate state. Start that checklist from now so the rows do not
    // backfill elapsed time and pop in already completed.
    let nextRevealAt = hasExistingLines
      ? now
      : isSigningIn
        ? Math.max(initialRevealAt, now)
        : initialRevealAt;

    return progressMessages.map((message, lineIndex) => {
      const lineKey = `${lineIndex}-${message}`;
      const existingTiming = committedTimingByKey.get(lineKey);

      if (existingTiming !== undefined) {
        nextRevealAt = Math.max(
          nextRevealAt,
          existingTiming.revealAt + existingTiming.holdMs + CHECKMARK_TO_NEXT_LINE_DELAY_MS,
        );
        return existingTiming;
      }

      const holdMs = getProgressLineHoldMs(message, progressMessages, destination);
      const lineTiming = {
        holdMs,
        revealAt: nextRevealAt,
      };
      nextRevealAt = lineTiming.revealAt + holdMs + CHECKMARK_TO_NEXT_LINE_DELAY_MS;

      return lineTiming;
    });
  }, [
    destination,
    greeting,
    isSigningIn,
    mountedAt,
    progressMessages,
    progressTimelineKey,
    startedAt,
  ]);
  React.useEffect(() => {
    const nextTimingByKey = new Map<string, ProgressLineTiming>();
    progressMessages.forEach((message, lineIndex) => {
      const lineTiming = lineRevealTimings[lineIndex];

      if (lineTiming !== undefined) {
        nextTimingByKey.set(`${lineIndex}-${message}`, lineTiming);
      }
    });
    lineRevealTimingByKey.current = nextTimingByKey;
    rememberProgressTimeline(progressTimelineKey, {
      maxProgressPercent:
        rememberedProgressTimelines.get(progressTimelineKey)?.maxProgressPercent ?? 0,
      timingByLineKey: nextTimingByKey,
    });
  }, [lineRevealTimings, progressMessages, progressTimelineKey]);
  const totalLineCount = progressMessages.length;
  const progressLineCount = getProgressLineCount({
    isSigningIn,
    progressMessages,
  });
  const hasStableProgressScope = progressLineCount > 0 && progressMessages.length > 0;
  const targetCompletedLineCount = isReady
    ? totalLineCount
    : getCompletedLineCount(progressMessages);
  const timelineVisibleLineCount = lineRevealTimings.reduce(
    (visibleCount, lineTiming) =>
      Date.now() >= lineTiming.revealAt ? visibleCount + 1 : visibleCount,
    0,
  );
  const visibleLineCount = Math.min(
    Math.max(animatedVisibleLineCount, timelineVisibleLineCount),
    totalLineCount,
  );
  const timelineCompletedLineCount = lineRevealTimings.reduce((completedCount, lineTiming) => {
    if (completedCount >= targetCompletedLineCount) {
      return completedCount;
    }

    return Date.now() >= lineTiming.revealAt + lineTiming.holdMs
      ? completedCount + 1
      : completedCount;
  }, 0);
  const completedLineCount = Math.min(
    Math.max(animatedCompletedLineCount, timelineCompletedLineCount),
    targetCompletedLineCount,
    totalLineCount,
  );
  const hasCompletedVisualSequence = isReady && completedLineCount >= totalLineCount;
  const isIndeterminate = visibleLineCount === 0 && !hasCompletedVisualSequence;
  const isProgressIndeterminate =
    (!hasStableProgressScope || visibleLineCount === 0) && !hasCompletedVisualSequence;
  const currentProgressLineCount = isProgressIndeterminate
    ? 0
    : Math.min(Math.max(visibleLineCount, 1), progressLineCount);
  const footerProgressLineCount = hasCompletedVisualSequence
    ? progressLineCount
    : completedLineCount;
  const rawProgressPercent =
    progressLineCount === 0 || !hasStableProgressScope
      ? isReady
        ? 100
        : 0
      : Math.round((footerProgressLineCount / progressLineCount) * 100);
  const maxAlignedProgressPercent = rawProgressPercent;
  const progressRunSignature = `${startedAt}:${destination ?? 'unknown'}:${
    isSigningIn ? 'signing-in' : 'loading'
  }:${headlineMessage ?? ''}:${progressMessages[0] ?? ''}`;
  const rememberedProgressPercent =
    rememberedProgressTimelines.get(progressTimelineKey)?.maxProgressPercent ?? 0;
  const alignedRememberedProgressPercent = Math.min(
    rememberedProgressPercent,
    maxAlignedProgressPercent,
  );
  const alignedCurrentProgressPercent =
    progressPercentRef.current?.signature === progressRunSignature
      ? Math.min(progressPercentRef.current.percent, maxAlignedProgressPercent)
      : 0;
  const progressPercent =
    progressPercentRef.current?.signature === progressRunSignature
      ? Math.max(
          alignedCurrentProgressPercent,
          rawProgressPercent,
          alignedRememberedProgressPercent,
        )
      : Math.max(rawProgressPercent, alignedRememberedProgressPercent);
  progressPercentRef.current = {
    percent: progressPercent,
    signature: progressRunSignature,
  };
  React.useEffect(() => {
    const currentTimeline = rememberedProgressTimelines.get(progressTimelineKey);

    if (!currentTimeline) {
      rememberProgressTimeline(progressTimelineKey, {
        maxProgressPercent: progressPercent,
        timingByLineKey: new Map(lineRevealTimingByKey.current),
      });
      return;
    }

    currentTimeline.maxProgressPercent = Math.max(
      currentTimeline.maxProgressPercent,
      progressPercent,
    );
  }, [progressPercent, progressTimelineKey]);
  const meterLabel = getProgressMeterLabel({
    currentLineCount: currentProgressLineCount,
    isIndeterminate: isIndeterminate || !hasStableProgressScope,
    isSigningIn,
    isReady: isVisualReady,
    totalLineCount: progressLineCount,
  });
  const screenReaderLabel = getScreenReaderLabel({
    currentLineCount: currentProgressLineCount,
    isIndeterminate: isIndeterminate || !hasStableProgressScope,
    isSigningIn,
    isReady: isVisualReady,
    progressMessages,
    stallLevel,
    totalLineCount: progressLineCount,
  });
  const isAuthRulePending = isSigningIn && progressMessages.length === 0 && isAwaitingSignIn;
  const activeLineTiming =
    totalLineCount > 0
      ? lineRevealTimings[Math.min(completedLineCount, totalLineCount - 1)]
      : undefined;
  const stallClockStartedAt = isIndeterminate
    ? startedAt
    : (activeLineTiming?.revealAt ?? startedAt);
  React.useEffect(() => {
    setAnimatedVisibleLineCount((currentCount) =>
      Math.min(Math.max(currentCount, visibleLineCount), totalLineCount),
    );
  }, [totalLineCount, visibleLineCount]);

  React.useEffect(() => {
    if (animatedVisibleLineCount >= totalLineCount) {
      return undefined;
    }

    const nextLineTiming = lineRevealTimings[animatedVisibleLineCount];

    if (!nextLineTiming) {
      return undefined;
    }

    const revealDelay = Math.max(0, nextLineTiming.revealAt - Date.now());
    const revealTimer = window.setTimeout(() => {
      setAnimatedVisibleLineCount((currentCount) =>
        currentCount < totalLineCount ? Math.min(currentCount + 1, totalLineCount) : currentCount,
      );
    }, revealDelay);

    return () => {
      window.clearTimeout(revealTimer);
    };
  }, [animatedVisibleLineCount, lineRevealTimings, totalLineCount]);

  React.useEffect(() => {
    setAnimatedCompletedLineCount((currentCount) =>
      Math.min(
        Math.max(currentCount, completedLineCount),
        targetCompletedLineCount,
        totalLineCount,
      ),
    );
  }, [completedLineCount, targetCompletedLineCount, totalLineCount]);

  React.useEffect(() => {
    if (animatedCompletedLineCount >= targetCompletedLineCount) {
      return undefined;
    }

    const nextLineTiming = lineRevealTimings[animatedCompletedLineCount];

    if (!nextLineTiming) {
      return undefined;
    }

    const completionDelay = Math.max(
      0,
      nextLineTiming.revealAt + nextLineTiming.holdMs - Date.now(),
    );
    const completionTimer = window.setTimeout(() => {
      setAnimatedCompletedLineCount((currentCount) =>
        currentCount < targetCompletedLineCount
          ? Math.min(currentCount + 1, targetCompletedLineCount, totalLineCount)
          : currentCount,
      );
    }, completionDelay);

    return () => {
      window.clearTimeout(completionTimer);
    };
  }, [animatedCompletedLineCount, lineRevealTimings, targetCompletedLineCount, totalLineCount]);

  React.useEffect(() => {
    if (isReady) {
      setStallLevel(0);
      return undefined;
    }

    const elapsedMs = Math.max(0, Date.now() - stallClockStartedAt);
    const slowDelayMs = REASSURE_AFTER_MS - elapsedMs;
    const stalledDelayMs = RETRY_AFTER_MS - elapsedMs;
    const timers: number[] = [];

    setStallLevel(elapsedMs >= RETRY_AFTER_MS ? 2 : elapsedMs >= REASSURE_AFTER_MS ? 1 : 0);

    if (slowDelayMs > 0) {
      timers.push(
        window.setTimeout(() => {
          setStallLevel((currentLevel) => Math.max(currentLevel, 1));
        }, slowDelayMs),
      );
    }

    if (stalledDelayMs > 0) {
      timers.push(
        window.setTimeout(() => {
          setStallLevel(2);
        }, stalledDelayMs),
      );
    }

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [isReady, stallClockStartedAt]);

  React.useEffect(() => {
    if (!hasCompletedVisualSequence) {
      setIsVisualReady(false);
      setIsHandoff(false);
      return undefined;
    }

    // Complete the fill before changing the status, then leave Ready readable
    // before fading the loader out, as in the reference animation.
    const readyTimer = window.setTimeout(() => {
      setIsVisualReady(true);
    }, COMPLETION_HOLD_MS);
    const handoffTimer = window.setTimeout(() => {
      setIsHandoff(true);
    }, COMPLETION_HOLD_MS + READY_HOLD_MS);
    const completeTimer = window.setTimeout(
      () => {
        onHandoffComplete?.();
      },
      COMPLETION_HOLD_MS + READY_HOLD_MS + HANDOFF_MS,
    );

    return () => {
      window.clearTimeout(readyTimer);
      window.clearTimeout(handoffTimer);
      window.clearTimeout(completeTimer);
    };
  }, [hasCompletedVisualSequence, onHandoffComplete]);

  const loaderClassName = [
    'wv-page-loader',
    isAuthRulePending ? 'wv-page-loader--auth-pending' : 'wv-page-loader--auth-ready',
    isEntered ? 'is-entered' : undefined,
    isIndeterminate ? 'is-indeterminate' : undefined,
    isProgressIndeterminate ? 'is-progress-indeterminate' : undefined,
    isHeadlineRevealed ? 'is-greeted' : undefined,
    isVisualReady ? 'is-ready' : undefined,
    isHandoff ? 'is-handoff' : undefined,
    stallLevel > 0 ? 'is-slow' : undefined,
    stallLevel > 1 ? 'is-stalled' : undefined,
  ]
    .filter(Boolean)
    .join(' ');
  const visualStatusLabel = isVisualReady ? READY_STATUS_LABEL : statusLabel;
  const brandLogoClassName = [
    'wv-page-loader__brand-logo',
    brandLogoUri && loadedBrandLogoUri === brandLogoUri ? 'is-loaded' : undefined,
  ]
    .filter(Boolean)
    .join(' ');
  const watermarkLogoClassName = [
    'wv-page-loader__watermark',
    'wv-page-loader__watermark--image',
    watermarkLogoUri && loadedWatermarkLogoUri === watermarkLogoUri ? 'is-loaded' : undefined,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={loaderClassName}
      role="status"
      aria-live="polite"
      aria-busy={isVisualReady ? 'false' : 'true'}
      style={
        {
          '--wv-pl-entry-animation-offset': `${animationOffsetMs}ms`,
          '--wv-pl-greeting-animation-offset': `${headlineAnimationOffsetMs}ms`,
          '--wv-pl-progress': `${progressPercent}%`,
        } as React.CSSProperties
      }
    >
      <span className="wv-page-loader__sr-status">{screenReaderLabel}</span>
      <div className="wv-page-loader__aurora" aria-hidden="true" />
      {watermarkLogoUri ? (
        <img
          className={watermarkLogoClassName}
          src={watermarkLogoUri}
          alt=""
          aria-hidden="true"
          decoding="async"
          onError={handleWatermarkLogoError}
          onLoad={handleWatermarkLogoLoad}
        />
      ) : showFallbackBranding ? (
        <WavvestMark className="wv-page-loader__watermark" monochrome />
      ) : null}
      <div className="wv-page-loader__grain" aria-hidden="true" />
      <div className="wv-page-loader__vignette" aria-hidden="true" />

      <div className="wv-page-loader__frame">
        <div className="wv-page-loader__brand" aria-hidden="true">
          {brandLogoUri ? (
            <img
              className={brandLogoClassName}
              src={brandLogoUri}
              alt=""
              decoding="async"
              onError={handleBrandLogoError}
              onLoad={handleBrandLogoLoad}
            />
          ) : showFallbackBranding ? (
            <>
              <WavvestMark className="wv-page-loader__brand-mark" />
              <WavvestWordmark className="wv-page-loader__brand-word" />
            </>
          ) : null}
        </div>

        <div className="wv-page-loader__transmission">
          <p className="wv-page-loader__eyebrow" aria-hidden="true">
            <span className="wv-page-loader__pulse" />
            <span className="wv-page-loader__eyebrow-label">{visualStatusLabel}</span>
          </p>

          {greeting || fallbackHeadline ? (
            <h1 className="wv-page-loader__headline" aria-hidden="true">
              {greeting ? (
                <>
                  <span className="wv-page-loader__headline-line">{greeting.salutation}</span>
                  {greeting.name ? (
                    <span className="wv-page-loader__headline-line wv-page-loader__headline-line--name wv-page-loader__headline-name">
                      {greeting.name}
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="wv-page-loader__headline-line">{fallbackHeadline}</span>
              )}
            </h1>
          ) : null}

          <ul className="wv-page-loader__lines" aria-hidden="true">
            {progressMessages.map((message, lineIndex) => {
              const isComplete = lineIndex < completedLineCount;

              return (
                <ProgressLine
                  key={`${lineIndex}-${message}`}
                  isComplete={isComplete}
                  isVisible={lineIndex < visibleLineCount}
                  message={message}
                />
              );
            })}
          </ul>
        </div>

        <div className="wv-page-loader__footer" aria-hidden="true">
          <div className="wv-page-loader__track">
            <span className="wv-page-loader__fill" />
          </div>
          <p
            className="wv-page-loader__meter"
            data-pending-label={isSigningIn ? 'Authenticating' : 'Connecting'}
            data-step-label={
              progressLineCount > 0
                ? `Step ${progressLineCount} of ${progressLineCount}`
                : undefined
            }
          >
            {meterLabel}
          </p>
        </div>
      </div>
    </div>
  );
};
